import { createRpcBackend } from "@/backend/backendProvider";
import { captureExceptionWithContext } from "@/sentry";
import { createHandlerContext } from "@/stores/context";

export function almHandlerJira(
  remote,
  connectionId,
  almSessionId,
  context = createHandlerContext(),
) {
  // Connection methods route through the transport-switching backend (NATS when
  // the `useNats` flag is on and an adapter exists, else WAMP). Sync methods and
  // documented carve-outs stay on `remote` (WAMP). See jiraConnectionAdapters.ts.
  const backend = createRpcBackend(context);
  const jira = (method, args = [], kwargs, opts) =>
    backend.almToolCall("jira", method, args, kwargs, opts);

  function getIssueTypes(projectId) {
    return projectId === -1
      ? Promise.resolve([])
      : jira("get_issue_types", [connectionId], { project_id: projectId }).then((almIssueTypes) => {
          if (Array.isArray(almIssueTypes)) {
            return almIssueTypes.map((almIssueType) => ({
              id: almIssueType.id,
              name: almIssueType.name,
            }));
          } else {
            return [];
          }
        });
  }

  function getProjects() {
    return jira("get_projects", [connectionId]).then((projects) =>
      projects.map((project) => ({ id: project.id, name: project.name })),
    );
  }

  // Fetching every board on a large Jira instance can take longer than the default timeout.
  const allBoardsOpts = (projectId) => (projectId ? undefined : { timeout: 120_000 });

  // Kanban AND scrum boards. `board_types` is NATS-only (WAMP get_boards is
  // scrum-only), so force NATS regardless of the nats-piadmin flag.
  // When projectId is null, returns all boards on the connection.
  function fetchKanbanBoards(projectId = null) {
    return jira(
      "get_boards",
      [connectionId],
      {
        project_id: projectId,
        board_types: ["scrum", "kanban"],
        useBackend: 0,
      },
      allBoardsOpts(projectId),
    );
  }

  async function fetchKanbanBoardsForProjects(projectIds) {
    // Write results to a map keyed by board ID to avoid duplicates
    const boards = new Map();

    // One request per project, sequential to manage server/ALM load
    for (const projectId of new Set(projectIds.map(String))) {
      for (const board of await fetchKanbanBoards(projectId)) {
        if (!boards.has(board.id)) {
          // Jira Server/DC boards carry no `location`, so derive it from the project that was queried
          // (it's an approximation, but good enough for the user)
          boards.set(board.id, { ...board, location: board.location ?? { projectId } });
        }
      }
    }
    const result = [...boards.values()];

    // Fill in project names for boards that lack them
    if (result.some((board) => !board.location.projectName)) {
      // Names are only labels, so a failed lookup still returns the boards.
      const names = await getProjects()
        .then((projects) => new Map(projects.map(({ id, name }) => [String(id), name])))
        .catch((error) => {
          captureExceptionWithContext(error, { connectionId });
          return new Map();
        });
      for (const board of result) {
        board.location.projectName ??= names.get(String(board.location.projectId));
      }
    }

    return result;
  }

  return {
    name: "Jira",
    supportsLinks: true,
    checkStatus: () => {
      return jira("check_all_team_mappings", [connectionId]).then((res) => ({
        working: res.errored_teams.length === 0,
      }));
    },
    getIssueLinkTypes: () => {
      return jira("get_issue_link_types", [connectionId]).then((links) => {
        const types = links.issueLinkTypes.map((l) => ({
          link_type: "issue_link",
          issue_link_id: l.id,
          name: l.inward,
        }));
        types.unshift({
          link_type: "epic",
          issue_link_id: null,
          name: "links epic to\n(by epic link field)",
        });
        types.unshift({
          link_type: "parent",
          issue_link_id: null,
          name: "is parent of\n(by parent link field)",
        });
        return types;
      });
    },
    getIssueFields(project_id, issue_type_id) {
      return jira("get_fields", [connectionId], {
        project_id,
        issue_type_id,
      });
    },
    async getNumericIssueFields(project_id, issue_type_id) {
      const rawFields = await this.getIssueFields(project_id, issue_type_id);
      return Object.entries(rawFields)
        .filter(([, field]) => field.schema?.type === "number")
        .map(([key, { name }]) => ({ key, name }));
    },
    /**
     * Returns the Jira fields supported as session-backlog fields, filtered
     * server-side to only the schemas the sync engine can actually handle.
     * The backend requires a non-empty list of project ids.
     */
    getSessionBacklogFields(project_ids, issue_type_id) {
      return jira("get_session_backlog_fields", [connectionId], {
        project_ids,
        issue_type_id,
      });
    },
    getTeamMappingSources(teamId) {
      return jira("get_team_mapping_sources_formatted", [connectionId, +teamId]);
    },
    setTeamMappingSources(teamId, sources) {
      return jira(
        "set_team_mapping_sources_from_formatted",
        [connectionId, +teamId, sources],
        undefined,
        { throwing: true },
      );
    },

    getArtTeamFieldMapping(artId) {
      return jira("get_art_team_field_mapping_formatted", [connectionId, +artId]);
    },
    setArtTeamFieldMapping(artId, teamFieldMapping) {
      return jira(
        "set_art_team_field_mapping_from_formatted",
        [connectionId, +artId, teamFieldMapping],
        undefined,
        { throwing: true },
      );
    },
    getArtFieldMapping(artId) {
      return jira("get_art_field_mapping_formatted", [connectionId, +artId]);
    },
    setArtFieldMapping(artId, artFieldMapping) {
      return jira(
        "set_art_field_mapping_from_formatted",
        [connectionId, +artId, artFieldMapping],
        undefined,
        { throwing: true },
      );
    },
    getTeamFieldMappingForOneArtSession() {
      return jira("get_session_team_field_mapping_formatted", [almSessionId]);
    },
    setTeamFieldMappingForOneArtSession(teamFieldMapping) {
      return jira("set_session_team_field_mapping_from_formatted", [
        connectionId,
        almSessionId,
        teamFieldMapping,
      ]);
    },

    getArtTeamFieldMappingFields(project_ids) {
      return jira("get_art_team_field_mapping_fields", [connectionId], {
        project_ids,
      });
    },
    getTeamMappingFields(project_ids) {
      return jira("get_team_mapping_fields", [connectionId], {
        project_ids,
      });
    },
    getTeamDefaultFields(project_ids) {
      return jira("get_team_default_fields", [connectionId], {
        project_ids,
      });
    },
    getMapping: () => {
      return jira("get_board_mapping", [connectionId]);
    },
    getProjects,
    getBoards(projectId) {
      return jira(
        "get_boards",
        [connectionId],
        { project_id: projectId },
        allBoardsOpts(projectId),
      );
    },
    // Boards for the Kanban column mapping: kanban AND scrum boards (unlike the
    // scrum-only team board picker).
    // When projectIds are provided, only returns boards referencing any of projectIds.
    // An empty list returns no boards.
    // When omitted, returns all boards on the connection.
    getKanbanBoards(projectIds) {
      return projectIds ? fetchKanbanBoardsForProjects(projectIds) : fetchKanbanBoards();
    },
    // Per-ART reference Kanban board (Solution Train / multi-ART case), configured
    // per ART in the ART detail Kanban tab.
    getArtKanbanBoard(artId) {
      return jira("get_art_kanban_board", [connectionId, artId]);
    },
    setArtKanbanBoard(artId, boardId) {
      return jira("set_art_kanban_board", [connectionId, artId], { board_id: boardId || null });
    },
    // Session-level reference Kanban board (single-ART session case). Persisted on
    // pialmsync on the session doc, keyed by pi_session id (lives in the almsync
    // module, so routed via almSyncCall rather than the connection module).
    getSessionKanbanBoard(sessionId) {
      return backend.almSyncCall("jira", "session.get_session_kanban_board", [sessionId]);
    },
    setSessionKanbanBoard(sessionId, boardId) {
      return backend.almSyncCall("jira", "session.set_session_kanban_board", [sessionId], {
        board_id: boardId || null,
      });
    },
    getWebhookUrl: () => {
      return jira("get_webhook_endpoint", [], { connection_id: connectionId });
    },
    getIssueTypes,
    getPortfolioIssueTypes: getIssueTypes,
    checkLinkType: (link) => {
      const from_projects = link.from.projects.map((p) => p.id);
      const to_projects = link.to.projects.map((p) => p.id);
      return jira("check_link_type", [
        connectionId,
        link.from.almId,
        link.to.almId,
        link.type.link_type,
        from_projects,
        to_projects,
      ]);
    },
    getAlmTypes: () => {
      return backend.almSyncCall("jira", "session.get_alm_types", [almSessionId]).then((almTypes) =>
        almTypes.map((almType) => ({
          id: almType.id,
          issue_type: almType.issue_type,
          project_id: almType.project_id,
          wsjf: () => almType.jira_WSJF_field,
          jira_session_fields: almType.jira_session_fields || [],
        })),
      );
    },
    getTeamBoardAlmType: (almType) => {
      return almType ? { issueType: almType.issue_type } : {};
    },
    getBacklogBoardAlmType: (_sticky, almType) => {
      return almType
        ? {
            selectedField: almType.wsjf(),
            jiraSessionFields: almType.jira_session_fields,
          }
        : {};
    },
    getLinkTypes: () => {
      return backend
        .almSyncCall("jira", "session.get_link_types", [almSessionId])
        .then((almLinkTypes) =>
          almLinkTypes.map((almLinkType) => ({
            pi_link_type_id: almLinkType.pi_link_type_id,
            issue_link_id: almLinkType.issue_link_id,
            id: almLinkType.id,
            type: almLinkType.type,
          })),
        );
    },
    deleteLinkType: (link) => {
      // WAMP passes only the link-type id; the alm-sync session id is threaded
      // out-of-band for the NATS subject (ignored on the WAMP wire).
      return backend.almSyncCall(
        "jira",
        "session.delete_link_type",
        [link.almId],
        undefined,
        almSessionId,
      );
    },
    updateLinkType: (link) => {
      return backend.almSyncCall(
        "jira",
        "session.update_link_type",
        [link.almId, { issue_link_id: link.type.issue_link_id, type: link.type.link_type }],
        undefined,
        almSessionId,
      );
    },
    createLinkType: (link) => {
      // This ALM mapping call uses the legacy WAMP payload: existing PI link
      // type id plus selected Jira link metadata. NATS ALM-sync create currently
      // has a different from/to-sticky contract, so SwitchingRpcBackend falls
      // back to WAMP for this method.
      return backend.almSyncCall(
        "jira",
        "session.create_link_type",
        [almSessionId, link.id, link.type.link_type, link.type.issue_link_id],
        undefined,
        almSessionId,
      );
    },
    getTags: () => {
      return Promise.resolve([]);
    },
    loadSourceSprints: async (source, isCreating) => {
      if (!source.board_id) {
        return;
      }
      const res = await jira("get_sprints", [], {
        connection_id: connectionId,
        board_id: source.board_id,
      });
      const noSync = [{ name: "Don't sync to Jira", id: null }];
      const createSprint = isCreating ? [{ name: "Create new Sprint for me in Jira", id: -2 }] : [];
      source.sprints = [...noSync, ...createSprint, ...extractSprints(res)];

      function extractSprints(res) {
        if (typeof res === "object" && "success" in res) {
          if (!res.success) {
            throw new Error(res.error || "Could not load sprints");
          }
          return res.sprints || [];
        }
        return res;
      }
    },
    setSourceSprint: async (source, sessionSource) => {
      source.sprint = sessionSource.sprint ?? null;
      if (source.sprint) {
        await checkIfSelectedSprintInAvailableSprints(source);
      }
    },
    checkIssueTypes: (issueTypeId, projectIds) => {
      return jira("check_issue_type_fields", [], {
        connection_id: connectionId,
        issue_type_id: issueTypeId,
        projects: projectIds,
      });
    },
    getEpicIssueType: () => {
      return jira("get_epic_issue_type", [connectionId]);
    },
    getCustomEpicIssueTypeId: () => {
      return jira("get_custom_epic_issue_type", [connectionId]);
    },
    setCustomEpicIssueTypeId: (issueTypeId) => {
      return jira("set_custom_epic_issue_type", [connectionId, issueTypeId]);
    },
    checkProjectPermissions(projectId) {
      return jira("check_project_permissions", [connectionId, projectId]);
    },
    checkProjectPermissionsForArt(projectId) {
      return jira("check_project_permissions_for_art", [connectionId, projectId]);
    },
    checkAllTeamMappings({ cachedOnly = false } = {}) {
      return jira("check_all_team_mappings", [connectionId], { cached_only: cachedOnly });
    },
    checkAllProjectPermissionsForArt({ cachedOnly = false } = {}) {
      return jira("check_all_project_permissions_for_art", [connectionId], {
        cached_only: cachedOnly,
      });
    },
    createTestIssue({
      project_id,
      issue_type_id,
      default_fields,
      team_fields,
      art_fields,
      art_team_fields,
    }) {
      return jira("create_test_issue_from_client_format", [
        connectionId,
        project_id,
        issue_type_id,
        default_fields,
        team_fields,
        art_fields,
        art_team_fields,
        true,
      ]);
    },
    checkIssueType(issueTypeId, artIds) {
      return jira("check_issue_type_for_arts", [connectionId, issueTypeId, artIds]);
    },
    finishOauth(hostname) {
      return jira("finish_oauth", [], {
        hostname,
        connection_id: connectionId,
      });
    },
    getOauthUrl(hostname) {
      return jira("get_oauth_url", [], {
        hostname,
        connection_id: connectionId,
      });
    },
    getOauthPublicKey() {
      return jira("get_oauth_pubkey", []);
    },
    getOauthConsumerKey() {
      return jira("get_oauth_consumer_key", [], { connection_id: connectionId });
    },
    setPassword(password) {
      return jira("set_password", [], {
        password,
        connection_id: connectionId,
      });
    },
    setCloudServiceAccountToken(token) {
      return jira("set_service_account_token", [], {
        token,
        connection_id: connectionId,
      });
    },
  };

  async function checkIfSelectedSprintInAvailableSprints(source) {
    if (source.sprints.length > 0) {
      // exclude sprints that caught error in getJiraSprintsForUser
      const available = source.sprints.some((s) => s.id === source.sprint);
      if (!available) {
        let name;
        try {
          const sprint = await jira("get_sprint", [], {
            connection_id: connectionId,
            sprint_id: source.sprint,
          });
          name = sprint.name ? sprint.name + " [closed]" : source.sprint + " [not found]";
        } catch (err) {
          void captureExceptionWithContext(err, { source });
          name = source.sprint + " [problem]";
        }
        source.sprints.push({ id: source.sprint, name });
      }
    }
  }
}
